import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { COMMERCIAL_OFFER, isOctoberOfferActive } from "@shared/commercialOffer";
import { trackAeoLondonConversion, trackCalendlyClick } from "@/lib/analytics";

const BOOKING_URL = "https://calendly.com/modielnadi/30min?utm_source=site&utm_campaign=aeo-london";
const SCORE_URL = "/free-ai-growth-audit/?src=aeo-london";

export function getAeoLondonAuditOffer(now: Date = new Date()) {
  const promotionActive = isOctoberOfferActive(now);
  return {
    promotionActive,
    price: promotionActive ? COMMERCIAL_OFFER.audit.octoberPrice : COMMERCIAL_OFFER.audit.listPrice,
  };
}

export default function AeoLondonOfferCard() {
  // The live London-time check hides this message after 31 October 2026.
  const [offer, setOffer] = useState(() => getAeoLondonAuditOffer());

  useEffect(() => {
    setOffer(getAeoLondonAuditOffer());
  }, []);

  return (
    <aside className="rounded-2xl border border-[#FF8C00]/35 bg-[#111b27] p-6 shadow-[0_18px_45px_rgba(0,0,0,0.2)]" aria-label="AEO assessment options">
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#ffad4d]">Senior-led starting point</p>
      <p className="mt-3 font-serif text-3xl font-bold text-white">AI Visibility Audit</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-300">A deeper diagnostic for teams deciding whether to invest in a focused AEO action plan or a wider programme.</p>
      <div className="mt-4 flex flex-wrap items-end gap-x-3 gap-y-1" aria-live="polite">
        <p className="font-serif text-4xl font-bold text-white">{offer.price}</p>
        <p className="pb-1 text-sm text-slate-300">one-time, excluding VAT</p>
      </div>
      {offer.promotionActive ? (
        <p className="mt-3 text-sm leading-relaxed text-[#ffd7a3]">October: 15% off month 1 of Foundation or Growth, and 15% off the £750 AI Visibility Audit (£638). Ends 31 Oct, 23:59 UK. <a className="font-semibold underline underline-offset-4 hover:text-white" href="/pricing/">View pricing</a>.</p>
      ) : (
        <p className="mt-3 text-sm leading-relaxed text-slate-300">Scope and programme fit are confirmed after assessment. <a className="text-[#ffad4d] underline-offset-4 hover:underline" href="/pricing/">View flexible starting points</a>.</p>
      )}
      <ul className="mt-5 space-y-2 text-sm text-slate-200">
        {["Defined around your commercial context", "Clear priorities and next-step options", "No rankings, citations, lead or revenue guarantees"].map((item) => (
          <li key={item} className="flex gap-2"><CheckCircle2 className="mt-0.5 size-4 shrink-0 text-[#ffad4d]" aria-hidden="true" /><span>{item}</span></li>
        ))}
      </ul>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <a href={SCORE_URL} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#FF8C00] px-4 py-3 text-center text-sm font-bold text-[#111827] transition hover:bg-[#ffad4d] focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 focus:ring-offset-[#111b27]" onClick={() => trackAeoLondonConversion({ action: "cta_click", placement: "offer_card_score" })}>
          Check my AI visibility<ArrowRight className="size-4" aria-hidden="true" />
        </a>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center justify-center rounded-lg border border-white/20 px-4 py-3 text-center text-sm font-semibold text-white transition hover:border-[#FF8C00] hover:text-[#ffad4d] focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:ring-offset-2 focus:ring-offset-[#111b27]" onClick={() => { trackCalendlyClick("aeo_london_offer_card"); trackAeoLondonConversion({ action: "booking_click", placement: "offer_card" }); }}>
          Book a 30-minute call
        </a>
      </div>
    </aside>
  );
}
